import { useCallback, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { Box, Grid } from "@mui/material";
import ModalComponent from "components/Modal";
import type { StoreType } from "types/genericTypes";
import { SharedTable } from "../../../shared/table";
import type { FilaInventario, QrProducto } from "../types/catalogoInventario.types";
import { useCatalogoInventario } from "../hooks/useCatalogoInventario";
import { useFormularioInventario } from "../hooks/useFormularioInventario";
import { useTablaInventario } from "../hooks/useTablaInventario";
import { useSeleccionInventario } from "../hooks/useSeleccionInventario";
import { useGenerarQrMasivo } from "../hooks/useGenerarQrMasivo";
import { ANCHO_MINIMO_INVENTARIO, type AccionesInventario } from "./CatalogoInventarioColumnas";
import { CatalogoInventarioAcciones } from "./CatalogoInventarioAcciones";
import { CatalogoInventarioCuadricula } from "./CatalogoInventarioCuadricula";
import { FormularioInventarioModal } from "./FormularioInventarioModal";
import { DetalleProductoModal } from "./DetalleProductoModal";

export const CatalogoInventario = (): JSX.Element => {
    const intl = useIntl();
    const idUsuario = useSelector((state: StoreType) => Number(state?.app?.user?.data?.id_usuario) || 0);

    const datos = useCatalogoInventario(idUsuario);
    const formulario = useFormularioInventario({ onCrear: datos.crear, onEditar: datos.editar });
    const seleccion = useSeleccionInventario();
    const [productoDetalle, setProductoDetalle] = useState<FilaInventario | null>(null);

    const ultimas = useRef({ abrirEditar: formulario.abrirEditar, activar: datos.activar, desactivar: datos.desactivar });
    ultimas.current = { abrirEditar: formulario.abrirEditar, activar: datos.activar, desactivar: datos.desactivar };

    const acciones = useMemo<AccionesInventario>(
        () => ({
            onVer: (fila) => setProductoDetalle(fila),
            onEditar: (fila) => ultimas.current.abrirEditar(fila),
            onActivar: (fila) => ultimas.current.activar(fila),
            onDesactivar: (fila) => ultimas.current.desactivar(fila),
        }),
        []
    );

    const { control, textos, visualizacion, cambiarVisualizacion } = useTablaInventario(
        datos.filas,
        acciones,
        seleccion
    );

    const { mostrarError, mostrarMensaje, asignarQrs } = datos;
    const onErrorQr = useCallback(
        (error: unknown) => mostrarError(error, "general_generar_qr_error"),
        [mostrarError]
    );
    const onQrGenerado = useCallback((qr: QrProducto) => asignarQrs([qr]), [asignarQrs]);

    const qrMasivo = useGenerarQrMasivo({
        idUsuario,
        onGenerados: asignarQrs,
        onMensaje: mostrarMensaje,
        onError: onErrorQr,
    });

    const generarQrSeleccionados = async () => {
        if (await qrMasivo.generar(seleccion.lista)) seleccion.limpiar();
    };

    const mensajeVacio = intl.formatMessage({ id: "no_hay_datos_registrados" });

    return (
        <Box sx={{ mt: 3 }}>
            <SharedTable
                control={control}
                cargando={datos.cargando}
                busqueda
                placeholderBusqueda={intl.formatMessage({ id: "input_buscador", defaultMessage: "Buscar…" })}
                anchoMinimo={ANCHO_MINIMO_INVENTARIO}
                mensajeVacio={mensajeVacio}
                acciones={
                    <CatalogoInventarioAcciones
                        visualizacion={visualizacion}
                        onCambiarVisualizacion={cambiarVisualizacion}
                        textoAnadir={intl.formatMessage({ id: "añadir_producto" })}
                        onAnadir={formulario.abrirCrear}
                        seleccionados={seleccion.cantidad}
                        generandoQr={qrMasivo.generando}
                        onGenerarQr={generarQrSeleccionados}
                        onLimpiarSeleccion={seleccion.limpiar}
                    />
                }
            >
                {visualizacion === "cuadricula" ? (
                    <CatalogoInventarioCuadricula
                        control={control}
                        acciones={acciones}
                        seleccion={seleccion}
                        textos={textos}
                        textoDesactivado={intl.formatMessage({ id: "producto_desactivado" })}
                        textoSinStock={intl.formatMessage({ id: "producto_sin_stock" })}
                        mensajeVacio={mensajeVacio}
                    />
                ) : undefined}
            </SharedTable>

            <ModalComponent handleClose={datos.cerrarMensaje} isOpen={datos.mensaje !== null} key="alerta-inventario">
                <Grid container spacing={2} sx={{ textAlign: "center", pt: 4 }}>
                    <Grid item xs={12}>
                        <p>{datos.mensaje}</p>
                    </Grid>
                </Grid>
            </ModalComponent>

            {/* AÑADIR NUEVO PRODUCTO */}
            <FormularioInventarioModal
                formulario={formulario}
                catalogos={datos.catalogos}
                guardando={datos.guardando}
            />
            {/* DETALLES DEL PRODUCTO INDIVIDUALMENTE */}
            <DetalleProductoModal
                key={productoDetalle?.id ?? "sin-producto"}
                producto={productoDetalle}
                idUsuario={idUsuario}
                onCerrar={() => setProductoDetalle(null)}
                onError={onErrorQr}
                onQrGenerado={onQrGenerado}
            />
        </Box>
    );
};
